import React, { useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Dropdown } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import UserAvatar from './Avatar.js';

function PawMark({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <ellipse cx="8" cy="11" rx="3.6" ry="2.6" />
      <ellipse cx="3.5" cy="6.5" rx="1.6" ry="2" />
      <ellipse cx="8"   cy="4"   rx="1.6" ry="2" />
      <ellipse cx="12.5" cy="6.5" rx="1.6" ry="2" />
    </svg>
  );
}

function Navbar(props) {
  const { t } = useTranslation();
  const location = useLocation();

  const isActive = useCallback((path) => {
    if (path === '/code/feed') {
      return (
        location.pathname.startsWith('/code/feed') ||
        (location.pathname.startsWith('/code/') &&
          location.pathname.endsWith('/view'))
      );
    }
    if (path === '/code/add') return location.pathname === '/code/add';
    return location.pathname === path;
  }, [location.pathname]);

  const userMenuItems = useMemo(() => {
    if (!props.userData.id) return [];
    const items = [
      {
        key: 'myCodes',
        label: (
          <Link to={`/code/feed/${props.userData.id}`}>
            {t('My codes')}
          </Link>
        ),
      },
      {
        key: 'notifications',
        label: (
          <Link to={`/user/${props.userData.id}/notifications`}>
            {t('Notifications')}
          </Link>
        ),
      },
    ];
    if (props.userData.role === 'ADMIN') {
      items.push({
        key: 'resetPassword',
        label: (
          <Link to="/user/admin-reset-password">
            {t('Reset user password')}
          </Link>
        ),
      });
    }
    items.push({ type: 'divider' });
    items.push({
      key: 'logOut',
      label: <Link to="/logout">{t('Log out')}</Link>,
    });
    return items;
  }, [props.userData.id, props.userData.role, t]);

  return (
    <header className="cs-topbar">
      <Link to="/" className="cs-brand">
        <div className="cs-brand__mark">
          <PawMark size={16} />
        </div>
        <div className="cs-brand__word">
          codesniffer<span className="dot">.</span>
        </div>
      </Link>

      <nav className="cs-topbar__nav">
        <Link
          to="/code/feed"
          className={`cs-tabbtn${isActive('/code/feed') ? ' active' : ''}`}
        >
          <ListIcon />
          {t('Code feed')}
        </Link>
        <Link
          to="/code/add"
          className={`cs-tabbtn${isActive('/code/add') ? ' active' : ''}`}
        >
          <PlusIcon />
          {t('Add code')}
        </Link>
      </nav>

      <div className="cs-topbar__spacer" />

      {/* Theme toggle — segmented control */}
      {props.onSetTheme && (
        <div className="cs-theme-toggle" role="group" aria-label="Theme">
          <button
            type="button"
            className={
              `cs-theme-toggle__btn` +
              (props.theme === 'dark' ? ' active' : '')
            }
            onClick={() => props.onSetTheme('dark')}
            title={t('Dark theme')}
            aria-pressed={props.theme === 'dark'}
          >
            <MoonIcon />
            <span>{t('Dark')}</span>
          </button>
          <button
            type="button"
            className={
              `cs-theme-toggle__btn` +
              (props.theme === 'light' ? ' active' : '')
            }
            onClick={() => props.onSetTheme('light')}
            title={t('Light theme')}
            aria-pressed={props.theme === 'light'}
          >
            <SunIcon />
            <span>{t('Light')}</span>
          </button>
        </div>
      )}

      {props.userData.id ? (
        <>
          <Link
            to={`/user/${props.userData.id}/notifications`}
            className="cs-iconbtn"
            title={t('Notifications')}
            style={{ marginLeft: 4 }}
          >
            <Badge count={props.newNotificationsCount} size="small">
              <GlobalOutlined
                style={{ color: 'var(--fg-2)', fontSize: 16 }}
              />
            </Badge>
          </Link>

          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={['click']}
            overlayClassName="cs-user-dropdown"
          >
            <button
              type="button"
              className="cs-topbar__user"
              aria-label={t('User menu')}
            >
              <UserAvatar
                user={props.userData.id}
                email={props.userData.email}
                size="sm"
              />
              <span className="name">{props.userData.id}</span>
              <ChevDown />
            </button>
          </Dropdown>
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            marginLeft: 8,
          }}
        >
          <Link
            to="/login"
            className="cs-btn cs-btn--ghost cs-btn--sm"
          >
            {t('Log in')}
          </Link>
          <Link to="/register" className="cs-btn cs-btn--sm">
            {t('Register')}
          </Link>
        </div>
      )}
    </header>
  );
}

function ListIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ChevDown() {
  return (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

export default Navbar;
